// Pure derivation helpers + shared types for the per-work-order Labels + Stars
// feature.
//
// Display state is read directly from the work order JSON (`workOrder.labels` /
// `workOrder.stars`, both indexed server-side and present in the search + detail
// responses). After a mutation the calling component persists via the API thunk
// and dispatches the generated WORK_ORDERS updatePartialSuccess action to merge the
// new stars/labels into the cached work order, so every surface re-renders from Redux
// — no client-side cache needed.

import posthog from 'posthog-js';
import moment from 'moment-timezone';
import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';
import { retrieveCachedUserDetails } from '../thunks/session_thunks';

// Feature flag gating the entire Work Order Labels + Stars feature (track/untrack, label apply,
// label chips, the labels/starred filters, the Tracked tab, and the labels management page). When
// off, every star/label surface is hidden. Single source of the flag name.
export const WORK_ORDER_LABELS_TRACK_FLAG = 'workorder-labels-track';
export const isWorkOrderLabelsTrackEnabled = (): boolean =>
	!!posthog.isFeatureEnabled(WORK_ORDER_LABELS_TRACK_FLAG);

// Reminders sit on top of tracking, so they need both flags. Off, reminders are hidden, not cleared:
// a reminder set earlier stays saved (a note edit still sends it back) and returns with the flag.
export const WORK_ORDER_REMINDERS_FLAG = 'Work-Order-Reminders';
export const isWorkOrderRemindersEnabled = (): boolean =>
	isWorkOrderLabelsTrackEnabled() && !!posthog.isFeatureEnabled(WORK_ORDER_REMINDERS_FLAG);

// Mirrors the backend models (openwrench-app: WorkOrderLabel / WorkOrderUserStar).
export interface WorkOrderLabel {
	id: number;
	label: string;
	// Optional on the backend (Option[String]); always sent when created via the UI.
	color?: string;
	buyerCompanyId?: number;
	supplierFacilityId?: number;
	associatedWorkOrdersCount?: number;
}

// Per-user bookmark on a work order. The backend returns ONLY the current user's star in
// the search + detail payloads (other users' stars/notes are stripped server-side — see
// WorkOrderServiceImpl.getWorkOrderOSDocFromSource / hydrateWorkOrders). The email match in
// getWorkOrderStarState is just defensive selection of that entry, not the privacy boundary
// — client filtering must never be relied on to hide another user's private note.
export interface WorkOrderUserStar {
	email: string;
	workOrderId: number | string;
	starNote?: string | null;
	// Epoch millis in search results (OpenSearch doc), ISO string everywhere else.
	remindAt?: number | string | null;
	// "Remind me only if…": free text the backend keeps with the reminder (needs a remindAt).
	remindCondition?: string | null;
	createdAt?: string;
	updatedAt?: string;
}

export type WorkOrderStarState = {
	starred: boolean;
	note: string | null;
	remindAt: moment.Moment | null;
	condition: string | null;
};

// Mirrors WorkOrderUserStar.ReminderStatus — also the values of the
// `reminderStatusForCurrentUser` search filter.
export const REMINDER_STATUS = {
	ACTIVE: 'active',
	UPCOMING: 'upcoming',
	NONE: 'none',
} as const;

export type ReminderStatus = typeof REMINDER_STATUS[keyof typeof REMINDER_STATUS];
// Mirrors WorkOrderUserStar.Action — returned by PUT /star and recorded on star log rows
// as `starAction`.
export const STAR_ACTION = {
	STARRED: 'starred',
	UNSTARRED: 'unstarred',
	NOTE_UPDATED: 'note_updated',
	REMINDER_SET: 'reminder_set',
	REMINDER_UPDATED: 'reminder_updated',
	REMINDER_SNOOZED: 'reminder_snoozed',
	REMINDER_STOPPED: 'reminder_stopped',
	REMINDER_REMOVED: 'reminder_removed',
};

// moment accepts both shapes the backend sends (epoch millis or ISO), so this is the one
// place either is turned into a time.
export const parseRemindAt = (value: number | string | null | undefined): moment.Moment | null => {
	if (value === null || value === undefined || value === '') return null;
	const parsed = moment(value);
	return parsed.isValid() ? parsed : null;
};

// Active once it rings (see useReminderRinging — the backend fired it, not the clock), and
// stays active until the user snoozes, stops, or untracks.
export const getReminderStatus = (
	remindAt: moment.Moment | null,
	isRinging: boolean
): ReminderStatus => {
	if (!remindAt) return REMINDER_STATUS.NONE;
	return isRinging ? REMINDER_STATUS.ACTIVE : REMINDER_STATUS.UPCOMING;
};

export const SNOOZE_OPTIONS: { label: string; minutes: number }[] = [
	{ label: '15 minutes', minutes: 15 },
	{ label: '1 hour', minutes: 60 },
	{ label: '4 hours', minutes: 240 },
	{ label: '1 day', minutes: 1440 },
];

// Reminder times are shown in the viewer's own zone: a reminder is personal, not tied to the
// work order's location. Today's reminders read as a time alone ("4:30 PM").
export const formatReminderTime = (
	remindAt: moment.Moment,
	now: moment.Moment = moment()
): string =>
	remindAt.isSame(now, 'day')
		? remindAt.format('h:mm A')
		: remindAt.isSame(now, 'year')
		? remindAt.format('MMM D, h:mm A')
		: remindAt.format('MMM D, YYYY, h:mm A');

// "25m", "2h 30m", "3d 4h" — the time left until an upcoming reminder, rounded up to the minute
// so it never reads "0m" while still upcoming.
export const formatReminderCountdown = (
	remindAt: moment.Moment,
	now: moment.Moment = moment()
): string => {
	const totalMinutes = Math.max(1, Math.ceil(remindAt.diff(now, 'seconds') / 60));
	const days = Math.floor(totalMinutes / 1440);
	const hours = Math.floor((totalMinutes % 1440) / 60);
	const minutes = totalMinutes % 60;
	if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
	if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
	return `${minutes}m`;
};

// An upcoming reminder: "Reminder in 2h 30m", or "Reminder: 4:30 PM" once its time has passed
// but it hasn't rung — its condition is still being checked.
export const formatUpcomingReminder = (
	remindAt: moment.Moment,
	now: moment.Moment = moment()
): string =>
	remindAt.isAfter(now)
		? `Reminder in ${formatReminderCountdown(remindAt, now)}`
		: `Reminder: ${formatReminderTime(remindAt, now)}`;

export const currentUserEmail = (): string =>
	nullSafeGetOrElse('contact.email', retrieveCachedUserDetails(['contact']), '');

// --- Labels -----------------------------------------------------------------

export const getWorkOrderLabels = (workOrder: any): WorkOrderLabel[] =>
	nullSafeGetOrElse('labels', workOrder, []);

// Label add/remove is recorded as a work-order note, so it rides the existing WO
// notes WebSocket as a `new_note` event carrying `addedLabel` / `removedLabel` (the
// same fields MessageLog renders). This derives the `{ labels }` patch to merge into
// the cached work order so chips stay live on every open client — handled purely on
// the FE, mirroring how note display is. Returns null when the event is not a label
// change or is a no-op (already-present add / already-absent removal), making it safe
// against the acting client's own optimistic patch and re-delivered/echoed events.
export const getLabelSyncFromNoteEvent = (
	data: any,
	workOrder: any
): { labels: WorkOrderLabel[] } | null => {
	const addedLabel: WorkOrderLabel = nullSafeGet('addedLabel', data);
	const removedLabel: WorkOrderLabel = nullSafeGet('removedLabel', data);
	if (!addedLabel && !removedLabel) return null;
	const currentLabels = getWorkOrderLabels(workOrder);
	if (addedLabel) {
		if (currentLabels.some((label) => label.id === addedLabel.id)) return null;
		return { labels: [...currentLabels, addedLabel] };
	}
	if (!currentLabels.some((label) => label.id === removedLabel.id)) return null;
	return { labels: currentLabels.filter((label) => label.id !== removedLabel.id) };
};

// --- Stars ------------------------------------------------------------------

export const getWorkOrderStarState = (workOrder: any): WorkOrderStarState => {
	const stars: WorkOrderUserStar[] = nullSafeGetOrElse('stars', workOrder, []);
	const email = currentUserEmail();
	// Case-insensitive match: the backend matches stars with equalsIgnoreCase, so the
	// cached contact email and star.email can differ only by casing. A strict === would
	// then miss the user's own star and render it as unstarred with the note hidden.
	const mine = email ? stars.find((star) => star && compareStrings(star.email, email)) : undefined;
	return {
		starred: !!mine,
		note: mine ? nullSafeGet('starNote', mine) || null : null,
		remindAt: mine ? parseRemindAt(nullSafeGet('remindAt', mine)) : null,
		condition: mine ? nullSafeGet('remindCondition', mine) || null : null,
	};
};

// When the work order is scheduled, behind the "Past schedule" reminder chip: its own scheduledAt
// (the field the pastSchedule filter reads), else the service call appointment, which is where
// search results and detail pages carry it.
export const getWorkOrderScheduledAt = (workOrder: any): string | number | null =>
	nullSafeGet('scheduledAt', workOrder) ||
	nullSafeGet('lastServiceCall.serviceScheduledAt', workOrder) ||
	nullSafeGet('serviceScheduledAt', workOrder) ||
	null;

export const isWorkOrderStarred = (workOrder: any): boolean =>
	getWorkOrderStarState(workOrder).starred;
